import { useContext, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { classes } from "../../../../util/style-utils";
import useYesNoOptions from "../../../../hooks/useYesNoOptions";
import { v4 } from 'uuid';
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { GeaoContext } from "../../GeaoContextProvider";
import { update } from "../../../../api/onboarding";
import { GeaoPaths } from "../../constants/GeaoPaths";
import GeaoVerticalStepperLayout from "../shared/Layout/GeaoVerticalStepperLayout";
import PrincipalFlowVerticalStepperElements from "../../constants/PrincipalFlowVerticalStepperElements";
import useTitle from "../../../../hooks/useTitle";
import { PageStatus } from "../../hooks/useInterviewUpdater";
import { EMAIL_WITH_TLD_REGEX } from "../../../../constants/ValidationConstants";

const validationSchema = yup.object().shape({
    authorizedRepresentativeForOnboarding: yup.string().required("Please select an option."),
    delegateFirstName: yup.string().when('authorizedRepresentativeForOnboarding', {
        is: (value) => value === 'true',
        then: (schema) => schema.required("Please enter the authorized rep's first name."),
        otherwise: (schema) => schema,
    }),
    delegateLastName: yup.string().when('authorizedRepresentativeForOnboarding', {
        is: (value) => value === 'true',
        then: (schema) => schema.required("Please enter the authorized rep's last name."),
        otherwise: (schema) => schema,
    }),
    delegateEmailAddress: yup.string()
        .email("Please enter a valid email address.")
        .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address.")
        .when('authorizedRepresentativeForOnboarding', {
            is: (value) => value === 'true',
            then: (schema) => schema.required("Please enter the authorized rep's email address."),
            otherwise: (schema) => schema,
        }),
});

export default function PrincipalRequestAuthorizedRep() {
    useTitle("Request Authorized Rep");

    const { agentInterview, setAgentInterview } = useContext(GeaoContext);
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors }, watch, getValues, trigger } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            authorizedRepresentativeForOnboarding: agentInterview?.questionAnswers?.AuthorizedRepresentativeForOnboarding?.answer,
            delegateFirstName: agentInterview?.questionAnswers?.DelegateFirstName?.answer,
            delegateLastName: agentInterview?.questionAnswers?.DelegateLastName?.answer,
            delegateEmailAddress: agentInterview?.questionAnswers?.DelegateEmailAddress?.answer,
        }
    });

    const [buttonsDisabled, setButtonsDisabled] = useState(false);
    const interviewIsCompleted = agentInterview.outcome === "Complete";

    const yesNoOptions = useYesNoOptions();
    const watchAuthorizedRep = watch("authorizedRepresentativeForOnboarding");
    const [nextPathOnSubmit, setNextPathOnSubmit] = useState();

    useEffect(() => {
        if (watchAuthorizedRep === 'true') {
            setNextPathOnSubmit(GeaoPaths.PrincipalReviewAndSign);
        } else {
            setNextPathOnSubmit(GeaoPaths.PrincipalAgencyOfficeInformation);
        }
    }, [watchAuthorizedRep]);

    function onSubmit(values) {
        updateInterview(values, nextPathOnSubmit);
    }

    function onSaveAndExit() {
        trigger().then(validationPassed => {
            if (validationPassed) {
                updateInterview(getValues(), GeaoPaths.CreateYourProfileChecklist, true);
            }
        });
    }

    function updateInterview(values, nextPath, isSaveAndExit = false) {
        setButtonsDisabled(true);

        const questionAnswers = {
            ...agentInterview.questionAnswers,
            AuthorizedRepresentativeForOnboarding: { answer: values.authorizedRepresentativeForOnboarding ?? " " },
            DelegateFirstName: { answer: values.delegateFirstName ?? " " },
            DelegateLastName: { answer: values.delegateLastName ?? " " },
            DelegateEmailAddress: { answer: values.delegateEmailAddress ?? " " },
        }

        let activityRequest = null;
        if (isSaveAndExit) {
            activityRequest = {
                ActivityCategory: "GEAO_ONBOARDING",
                ActivitySubType: "SAVE_AND_EXIT",
                SourceApplication: "AGIWUI",
                AdditionalData: {
                    ComponentName: "ProducerPersonalInformation"
                }
            };
        }

        const updatedInterview = {
            identityType: "Agent",
            interview: {
                ...agentInterview,
                questionAnswers: questionAnswers,
                pageStatus: {
                    ...agentInterview.pageStatus,
                    "PrincipalRequestAuthorizedRep": PageStatus.Completed
                }
            },
            activityRequest: activityRequest
        }

        update(updatedInterview,
            interview => {
                setAgentInterview(interview);
                navigate(nextPath);
            },
            () => {
                setButtonsDisabled(false);
            });
    };

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={PrincipalFlowVerticalStepperElements}
            currentStepperItem="Request Authorized Rep"
            header="Request Authorized Rep"
        >
            <form className="form geao-form" onSubmit={handleSubmit(onSubmit)}>
                <p className="margin-top margin-bottom">
                    Principals can delegate an authorized representative to complete the agency, E&O, and banking sections of this application.
                    The principal will still have to review, eSign, and submit this application.
                </p>

                <fieldset role="radiogroup" className={classes("form-field radio-button-group", errors.authorizedRepresentativeForOnboarding && "form-field--error")}>
                    <legend>
                        Do you want an authorized representative to complete this application?
                    </legend>
                    <div className="radio-button-wrapper col-2">
                        {yesNoOptions?.map((option) => {
                            var id = `${option.label}-radio-${v4()}`;
                            return (
                                <div key={id}>
                                    <input
                                        id={id}
                                        type="radio"
                                        value={option.value}
                                        {...register("authorizedRepresentativeForOnboarding")}
                                    />
                                    <label className="radio" htmlFor={id}>
                                        <span>{option.label}</span>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                    {errors.authorizedRepresentativeForOnboarding &&
                        <span className="form-message">{errors.authorizedRepresentativeForOnboarding.message}</span>
                    }
                </fieldset>

                {watchAuthorizedRep === "true" &&
                    <>
                        <div className="flex-row">
                            <div className={classes("form-field", errors.delegateFirstName && "form-field--error")}>
                                <label htmlFor="delegate-first-name" className="text">
                                    Authorized Rep First Name
                                </label>
                                <input id="delegate-first-name" type="text" placeholder="First Name" {...register("delegateFirstName")} />
                                {errors.delegateFirstName &&
                                    <span className="form-message">{errors.delegateFirstName.message}</span>
                                }
                            </div>

                            <div className={classes("form-field", errors.delegateLastName && "form-field--error")}>
                                <label htmlFor="delegate-last-name" className="text">
                                    Authorized Rep Last Name
                                </label>
                                <input id="delegate-last-name" type="text" placeholder="Last Name" {...register("delegateLastName")} />
                                {errors.delegateLastName &&
                                    <span className="form-message">{errors.delegateLastName.message}</span>
                                }
                            </div>
                        </div>

                        <div className="flex-row">
                            <div className={classes("form-field", errors.delegateEmailAddress && "form-field--error")}>
                                <label htmlFor="delegate-email-address" className="text">
                                    Authorized Rep Business Email Address
                                </label>
                                <input id="delegate-email-address" type="email" placeholder="Email" {...register("delegateEmailAddress")} />
                                {errors.delegateEmailAddress &&
                                    <span className="form-message">{errors.delegateEmailAddress.message}</span>
                                }
                            </div>
                        </div>
                    </>
                }

                <div className="margin-top">
                    <div className="button-group btn--pull-right">
                        <button
                            type="submit"
                            className="btn btn--secondary btn--full-mobile"
                            onClick={onSaveAndExit}
                            disabled={buttonsDisabled || interviewIsCompleted}>
                            <span>Save and Exit</span>
                        </button>

                        <button
                            type="submit"
                            className="btn btn--primary btn--full-mobile"
                            disabled={buttonsDisabled || interviewIsCompleted}>
                            <span>Next</span>
                        </button>
                    </div>
                </div>
            </form>
        </GeaoVerticalStepperLayout>
    );
}